import React, {useMemo, useState} from "react";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Dropdown} from "primereact/dropdown";
import {Tag} from "primereact/tag";
import {articleOfVideo, isVideo} from "./help-cms";
import {TabHeader} from "./ui";

// Links an article with a video: the video stores the article it complements, so one video belongs to one article.
function LinkDialog({state, data, onHide, onSaved}) {
    const {contents, api} = data;
    const [articleSlug, setArticleSlug] = useState(null);
    const [videoSlug, setVideoSlug] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(false);

    React.useEffect(() => {
        if (!state) return;
        setArticleSlug(state.video?.relatedSlug || null);
        setVideoSlug(state.video?.slug || null);
        setError(false);
    }, [state]);

    const articles = contents.filter((item) => !isVideo(item)).map((item) => ({value: item.slug, label: item.title}));
    const videos = contents.filter(isVideo).map((item) => ({value: item.slug, label: item.title}));

    const save = async (unlink) => {
        setSaving(true);
        setError(false);
        try {
            const video = contents.find((item) => item.slug === videoSlug);
            // an article shows one video: release the previous video of the chosen article
            const previous = contents.find((item) => isVideo(item) && item.relatedSlug === articleSlug && item.slug !== videoSlug);
            if (previous && !unlink) await api.updateContent(previous, {relatedSlug: null});
            if (state.video && state.video.slug !== videoSlug) await api.updateContent(state.video, {relatedSlug: null});
            await api.updateContent(video, {relatedSlug: unlink ? null : articleSlug});
            onSaved();
            onHide();
        } catch (e) {
            setError(true);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog visible={Boolean(state)} onHide={onHide} className="rtl w-11 md:w-5" header={state?.video ? "ویرایش اتصال" : "اتصال جدید"}
                footer={<div className="flex flex-wrap justify-content-between gap-2">
                    <div>{state?.video && <Button className="p-button-text p-button-danger text-xs" label="حذف اتصال" disabled={saving} onClick={() => save(true)}/>}</div>
                    <div className="flex flex-row-reverse gap-2">
                        <Button className="text-xs" label="ذخیره اتصال" loading={saving} disabled={!articleSlug || !videoSlug} onClick={() => save(false)}/>
                        <Button className="p-button-text text-xs" label="انصراف" onClick={onHide}/>
                    </div>
                </div>}>
            <div className="flex flex-column gap-3">
                <div className="flex flex-column gap-1">
                    <label className="text-xs text-600">مقاله</label>
                    <Dropdown filter className="w-full" panelClassName="rtl" value={articleSlug} options={articles} placeholder="انتخاب مقاله" onChange={(e) => setArticleSlug(e.value)}/>
                </div>
                <div className="text-center text-primary">⇄</div>
                <div className="flex flex-column gap-1">
                    <label className="text-xs text-600">ویدئو</label>
                    <Dropdown filter className="w-full" panelClassName="rtl" value={videoSlug} options={videos} placeholder="انتخاب ویدئو" onChange={(e) => setVideoSlug(e.value)}/>
                </div>
                {error && <small className="p-error">ذخیره اتصال انجام نشد.</small>}
            </div>
        </Dialog>
    );
}

export default function RelationsTab({data, reload, onEdit}) {
    const {contents} = data;
    const [dialog, setDialog] = useState(null);
    const pairs = useMemo(() => contents
        .filter((item) => isVideo(item) && item.relatedSlug)
        .map((video) => [articleOfVideo(contents, video), video])
        .filter(([article]) => Boolean(article)), [contents]);

    return (
        <div className="flex flex-column gap-3">
            <TabHeader kicker="ارتباط محتواها" title="مقاله و ویدئو را به هم وصل کنید" description="با این اتصال، کاربر در همان صفحه مقاله ویدئوی آن را می‌بیند."/>

            <div className="surface-100 border-round-lg p-3 flex flex-wrap align-items-center gap-2 text-sm">
                <span className="text-500">نتیجه این تنظیم:</span>
                <b>مقاله «ساخت پروژه»</b><span>+</span><b>ویدئوی «ساخت پروژه»</b><span>←</span>
                <strong className="text-primary">نمایش ویدئو داخل همان مقاله</strong>
                <Button className="text-xs mr-auto" label="+ اتصال جدید" onClick={() => setDialog({})}/>
            </div>

            <div className="flex flex-column gap-2">
                {pairs.map(([article, video]) => (
                    <div key={video.documentId} className="surface-card border-1 border-200 border-round-xl p-3 flex flex-wrap align-items-center gap-3">
                        <div className="flex-1 min-w-14rem flex align-items-center gap-2">
                            <Tag value="مقاله"/><b className="text-sm">{article.title}</b>
                            <Button className="p-button-text p-button-sm text-xs" label="مشاهده" onClick={() => onEdit(article)}/>
                        </div>
                        <span className="text-primary">⇄</span>
                        <div className="flex-1 min-w-14rem flex align-items-center gap-2">
                            <Tag severity="info" value="ویدئو"/><b className="text-sm">{video.title}</b>
                            <Button className="p-button-text p-button-sm text-xs" label="مشاهده" onClick={() => onEdit(video)}/>
                        </div>
                        <Button className="p-button-outlined p-button-sm text-xs" label="ویرایش اتصال" onClick={() => setDialog({video})}/>
                    </div>
                ))}
                {!pairs.length && <div className="text-sm text-500 p-3">هنوز مقاله‌ای به ویدئو وصل نشده است.</div>}
            </div>
            <LinkDialog state={dialog} data={data} onHide={() => setDialog(null)} onSaved={reload}/>
        </div>
    );
}
